import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import { FileUploader } from '@cartwhl/ui/feature/FileUploader';
import Button from '@cartwhl/ui/input/Button';

import { animationGenerationStore } from 'stores';

import { MAX_FILE_SIZE, VIDEO_EXTENSIONS } from '../constants';

// This component doesn't trigger the upload just yet, we want to show the trimming modal first.
export default function VideoFilePicker({
  close,
  next,
}: {
  close: () => void;
  next: (videoFile: File, mediaID?: string) => void;
}) {
  const {
    existingMediaExtension,
    existingMediaID,
    existingMediaName,
    existingMediaURL,
    videoUpload,
  } = useSnapshot(animationGenerationStore.state);

  useEffect(() => {
    if (
      existingMediaExtension &&
      existingMediaID &&
      existingMediaName &&
      existingMediaURL
    ) {
      // Create a fake file object from the URL
      let contentType = 'video/mp4';
      fetch(existingMediaURL, {
        credentials: 'include',
      })
        .then((res) => {
          contentType = res.headers.get('Content-Type') || 'unknown';
          return res.blob();
        })
        .then(async (blob) => {
          const file = new File(
            [blob],
            `${existingMediaName}.${existingMediaExtension}`,
            {
              type: contentType,
            },
          );
          next(file, existingMediaID);
        });
    }
  }, [
    existingMediaExtension,
    existingMediaID,
    existingMediaName,
    existingMediaURL,
    next,
  ]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };

  return (
    <>
      <Text
        as="p"
        variant="studio/body/medium/sm"
        lg:variant="studio/body/medium/md"
        className="text-gray-60 dark:text-gray-45 mb-1.5 font-medium"
      >
        In effort to get you the best results, note the following requirements
        for video upload:
      </Text>
      <Text
        as="ul"
        variant="studio/body/medium/sm"
        lg:variant="studio/body/medium/md"
        className="text-gray-90 dark:text-gray-15 mb-5 list-disc pl-6 font-medium"
      >
        <li>Includes only 1 person.</li>
        <li>Is under 100MB.</li>
        <li>
          In the next step, you will have to trim the video to under 30 secs.
        </li>
      </Text>

      <div className="h-1/2">
        <FileUploader
          fileContent="Video"
          accept={{ 'application/octet-stream': VIDEO_EXTENSIONS }}
          cancelable={true}
          maxFileSize={MAX_FILE_SIZE}
          upload={videoUpload}
          onDrop={next}
          onCancel={(uploadID: string) => {
            animationGenerationStore.deleteJobFromBackend(uploadID);
            animationGenerationStore.removeUpload();
          }}
          onRemove={() => animationGenerationStore.removeUpload()}
        />
      </div>

      {!videoUpload && (
        <div className="mt-6">
          <Button onClick={() => handleOpenChange(false)}>Cancel</Button>
        </div>
      )}
    </>
  );
}
